/** 全站图表/图形调色板（单一来源）：「科技蓝青」主色 + AI 电光青 + 语义色，ECharts/图形统一引用
 *  注意：ECharts 在 canvas 内渲染，不解析 CSS 变量 —— 本文件色值一律字面量，禁止 var()。
 *
 *  **主题承载键与恒定键的区分（改动前必看）**：
 *  - 主题承载键 `PRIMARY` / `PURPLE` / `CYAN` 是**可变量**，由 `theme/applyTheme.ts` 的
 *    `syncChartTheme` 在应用主题时就地改写（ESM 实时绑定，调用方在渲染时读到当前主题值）；
 *    它们只对"应用主题之后发生的渲染"生效——已挂载组件的内联色与已渲染的 canvas 不会重绘，
 *    需要主题即时跟随的 DOM 元素请直接引用 `--bcc-*` CSS token，不要读本文件的常量。
 *  - `TEAL` / `NEUTRAL` 与成功/警告/危险系是**阶段色与功能色**，与主题无关，保持恒定。
 */
export let PRIMARY = '#2563eb';
export const SUCCESS = '#10b981';
export const WARNING = '#f59e0b';
export const DANGER = '#ef4444';
/** AI 专属色（AI=电光青 #06b6d4；历史命名 PURPLE 保留兼容，值随主题 AI 色走） */
export let PURPLE = '#06b6d4';
/** 青（渐变尾色/TTS 阶段色；与 AI 色同档，随主题走） */
export let CYAN = '#06b6d4';
/** 青碧（实时识别 LLM 阶段色）：识别阶段色板的一档，与主题主色解耦以免与 ASR/TTS 档混同 */
export const TEAL = '#14b8a6';
/** 中性灰蓝（技术值/未命中/弱化态） */
export const NEUTRAL = '#64748b';

/** 主题色同步入口：仅供 `theme/applyTheme.ts` 调用（页面/组件禁止直接调用） */
export function syncPaletteColors(theme: { primary: string; ai: string }): void {
  PRIMARY = theme.primary;
  PURPLE = theme.ai;
  CYAN = theme.ai;
  AGENT_STATE_COLORS.ringing = theme.primary;
}

/**
 * 流程设计器「出口线」色板（业务流程图：同一节点的每个出口一条线，颜色用于区分出口身份）。
 * - 语义色优先：`FLOW_EXIT_SEMANTIC` 命中的出口（true/false/ok/默认…）恒用固定语义色，
 *   与节点色/状态语义一致，不随节点出口数量变化。
 * - 其余出口按 `FLOW_EXIT_PALETTE` 顺序取色，分配时跳过同节点已占用的色，保证**同节点各出口不撞色**。
 * - 色值为字面量并与主题**解耦**：出口色是「分支身份」而非主题装饰，
 *   换主题不得让既有流程的出口配色漂移（对照本文件顶部主题承载键的说明）。
 */
export const FLOW_EXIT_PALETTE = [
  '#2563eb', // 蓝
  '#7c3aed', // 紫
  '#0891b2', // 青
  '#0d9488', // 蓝绿
  '#db2777', // 玫红
  '#65a30d', // 橄榄
  '#ea580c', // 橙
  '#4f46e5', // 靛
] as const;

/** 出口语义色：出口 id（condition 的 true/false、数据源状态、默认出口…）→ 线色 */
export const FLOW_EXIT_SEMANTIC: Record<string, string> = {
  true: '#10b981',
  yes: '#10b981',
  ok: '#10b981',
  pass: '#10b981',
  success: '#10b981',
  false: '#ef4444',
  no: '#ef4444',
  fail: '#ef4444',
  error: '#ef4444',
  timeout: '#ef4444',
  default: '#f59e0b', // 默认/失败重试/未命中：琥珀 + 虚线（与普通出口再加一层区分）
};

/** 出口无出边 / 未连线的警示色（画布角标） */
export const FLOW_WARN = '#d97706';
/** dry-run 执行路径高亮色 */
export const FLOW_PATH = '#10b981';

/** 语义色：分/阈值通用（得分越高越好） */
export const SCORE_COLORS = ['#ef4444', '#f59e0b', '#10b981'];

/** 满意度 1-5 分梯度色（1 最差 → 5 最好） */
export const SATISFACTION_COLORS = ['#ef4444', '#f59e0b', '#fbbf24', '#34d399', '#10b981'];

/** 分数 → 状态色（质检/统计通用口径：≥80 优 / ≥60 中 / <60 差） */
export function scoreColor(score: number | undefined | null): string {
  const s = Number(score);
  if (Number.isNaN(s) || s <= 0) return '#64748b';
  if (s >= 80) return SUCCESS;
  if (s >= 60) return WARNING;
  return DANGER;
}

/**
 * 趋势环比色（按指标好坏方向翻转）：传入 goodWhenUp 控制"上升是好是坏"。
 * 默认上升=好（绿涨红跌）；对转人工率/负面率等"上升为坏"指标传 false。
 */
export function trendColor(delta: number | undefined | null, goodWhenUp = true): string {
  const d = Number(delta) || 0;
  if (d === 0) return '#64748b';
  const up = d > 0;
  const good = goodWhenUp ? up : !up;
  return good ? SUCCESS : DANGER;
}

export function trendArrow(delta: number | undefined | null): string {
  const d = Number(delta) || 0;
  if (d === 0) return '';
  return d > 0 ? '↑' : '↓';
}

/** 坐席状态 → 状态点色（顶栏/状态墙统一来源） */
export const AGENT_STATE_COLORS: Record<string, string> = {
  idle: '#10b981',
  break: '#f59e0b',
  offline: '#64748b',
  ringing: '#2563eb',
  busy: '#ef4444',
  talking: '#ef4444',
  acw: '#fbbf24',
  after_work: '#fbbf24',
  after_call_work: '#fbbf24',
};
